import { NavLink } from 'react-router'
import { ChevronDown, ChevronRight, MoreHorizontal } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useState } from 'react'
import { getNavigationIcon, getNavigationItems } from '@/lib/navigation'
import { useAuth } from '@/providers/auth-provider'

interface PortalSidebarNavProps {
  collapsed: boolean
}

export function PortalSidebarNav({ collapsed }: PortalSidebarNavProps) {
  const { user } = useAuth()
  const [moreOpen, setMoreOpen] = useState(false)
  const navItems = getNavigationItems('client', user)
  const mainNavItems = navItems.slice(0, 8)
  const moreNavItems = navItems.slice(8)

  return (
    <nav className="flex flex-col gap-3 p-3">
      {!collapsed && (
        <div className="px-2">
          <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-sidebar-foreground/45">
            Workspace
          </p>
        </div>
      )}
      {mainNavItems.map(({ icon, label, href }) => {
        const Icon = getNavigationIcon(icon)
        return (
        <NavLink
          key={href}
          to={href}
          end={href === '/portal'}
          className={({ isActive }) =>
            cn(
              'group flex items-center gap-3 rounded-xl border border-transparent px-3 py-3 text-sm transition-all',
              'text-sidebar-foreground/84 hover:border-sidebar-border/70 hover:bg-white/70 hover:text-sidebar-accent-foreground',
              isActive &&
                'border-sidebar-primary/15 bg-sidebar-primary text-sidebar-primary-foreground font-medium shadow-[0_18px_30px_rgba(3,102,102,0.18)]',
              collapsed && 'justify-center px-2'
            )
          }
          title={collapsed ? label : undefined}
        >
          <Icon className="h-4 w-4 shrink-0 transition-transform group-hover:scale-105" />
          {!collapsed && <span>{label}</span>}
        </NavLink>
        )
      })}

      {/* "More" expandable group */}
      {!collapsed && moreNavItems.length > 0 && (
        <>
          <button
            onClick={() => setMoreOpen(o => !o)}
            className="flex w-full items-center gap-3 rounded-xl border border-transparent px-3 py-3 text-sm text-sidebar-foreground/84 transition-colors hover:border-sidebar-border/70 hover:bg-white/70 hover:text-sidebar-accent-foreground"
          >
            <MoreHorizontal className="h-4 w-4 shrink-0" />
            <span className="flex-1 text-left">More</span>
            {moreOpen ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
          </button>

          {moreOpen && (
            <div className="ml-7 flex flex-col gap-1 border-l border-sidebar-border/70 pl-3">
              {moreNavItems.map(({ icon, label, href }) => {
                const Icon = getNavigationIcon(icon)
                return (
                <NavLink
                  key={href}
                  to={href}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-3 rounded-lg px-2 py-2 text-sm transition-colors',
                      'text-sidebar-foreground/84 hover:bg-white/70 hover:text-sidebar-accent-foreground',
                      isActive && 'bg-white text-sidebar-accent-foreground font-medium shadow-sm'
                    )
                  }
                >
                  <Icon className="h-4 w-4 shrink-0" />
                  <span>{label}</span>
                </NavLink>
                )
              })}
            </div>
          )}
        </>
      )}

      {collapsed && moreNavItems.length > 0 && (
        <>
          {moreNavItems.map(({ icon, label, href }) => {
            const Icon = getNavigationIcon(icon)
            return (
            <NavLink
              key={href}
              to={href}
              className={({ isActive }) =>
                cn(
                  'flex items-center justify-center rounded-xl px-2 py-3 text-sm transition-colors',
                  'text-sidebar-foreground/84 hover:bg-white/70 hover:text-sidebar-accent-foreground',
                  isActive && 'bg-white text-sidebar-accent-foreground font-medium shadow-sm'
                )
              }
              title={label}
            >
              <Icon className="h-4 w-4 shrink-0" />
            </NavLink>
            )
          })}
        </>
      )}
    </nav>
  )
}
